import { z } from 'zod';

// Fellow application review (/api/review/staff, /api/review/reviewer, /api/review/auth).
// Shape only: who may do what stays in reviewPolicy.can, and the rubric, eligibility rules,
// settings ranges, scores and transitions are checked by the services. The lists below mirror
// models/enums.ts, the review models and the services (the tests compare them).

const UUID = /^[0-9a-f-]{36}$/i;
const Id = z.string().regex(UUID, 'is not an id');

export const ReviewCycleState = z.enum([
  'draft',
  'open',
  'intake_closed',
  'eligibility',
  'assignment',
  'review',
  'calibration',
  'deliberation',
  'decided',
  'archived',
]);
export type ReviewCycleState = z.infer<typeof ReviewCycleState>;

export const ReviewSeat = z.enum(['program_manager', 'chair', 'co_chair', 'reviewer', 'observer']);
export type ReviewSeat = z.infer<typeof ReviewSeat>;

export const ReviewOutcome = z.enum(['accepted', 'transferred', 'conditional', 'ineligible']);
export type ReviewOutcome = z.infer<typeof ReviewOutcome>;

export const ReviewConflictType = z.enum([
  'supervisor',
  'trainee',
  'family_personal',
  'financial',
  'same_institution',
  'coauthor',
]);
export type ReviewConflictType = z.infer<typeof ReviewConflictType>;

export const ReviewConflictStatus = z.enum(['open', 'allowed', 'treated_as_hard', 'dismissed']);
export type ReviewConflictStatus = z.infer<typeof ReviewConflictStatus>;

/** What a chair may rule on an open soft conflict. */
export const ReviewConflictRuling = z.enum(['allowed', 'treated_as_hard']);
export type ReviewConflictRuling = z.infer<typeof ReviewConflictRuling>;

export const ReviewAssignmentState = z.enum(['proposed', 'active', 'conflicted', 'removed']);
export type ReviewAssignmentState = z.infer<typeof ReviewAssignmentState>;

export const ReviewAssignmentPurpose = z.enum(['primary', 'third']);
export type ReviewAssignmentPurpose = z.infer<typeof ReviewAssignmentPurpose>;

export const ReviewConditionStatus = z.enum(['pending', 'met', 'not_met']);
export type ReviewConditionStatus = z.infer<typeof ReviewConditionStatus>;

export const ReviewEligibilityStatus = z.enum(['pending', 'eligible', 'flagged', 'ineligible']);
export type ReviewEligibilityStatus = z.infer<typeof ReviewEligibilityStatus>;

/** What the Program Manager may settle a flagged application as. */
export const ReviewEligibilityResolution = z.enum(['eligible', 'ineligible']);
export type ReviewEligibilityResolution = z.infer<typeof ReviewEligibilityResolution>;

export const ReviewRubricScale = z.enum(['lmh', 'count_lmh', 'score', 'text']);
export type ReviewRubricScale = z.infer<typeof ReviewRubricScale>;

export const ReviewRating = z.enum(['low', 'medium', 'high']);
export type ReviewRating = z.infer<typeof ReviewRating>;

export const ReviewEligibilityRuleType = z.enum(['program_or_ecr', 'answer_is_yes']);
export type ReviewEligibilityRuleType = z.infer<typeof ReviewEligibilityRuleType>;

/** Which of two submissions from the same applicant stays in the cycle. */
export const ReviewKeepSubmission = z.enum(['later', 'earlier']);
export type ReviewKeepSubmission = z.infer<typeof ReviewKeepSubmission>;

const Text = z.string().nullish();
/** Read with Number() by the service, which reports its own range message. */
const Num = z.union([z.number(), z.string(), z.null()]);
const Digits = z.string().regex(/^\d+$/, 'must be a whole number');
/** A list of emails, or one string split on spaces, commas and semicolons by the service. */
const EmailList = z.union([z.array(z.string()), z.string()]);

// ---------------------------------------------------------------- params

export const ReviewCycleParams = z.strictObject({ cycleId: Id });
export type ReviewCycleParams = z.input<typeof ReviewCycleParams>;

export const ReviewApplicationParams = z.strictObject({ cycleId: Id, applicationId: Id });
export type ReviewApplicationParams = z.input<typeof ReviewApplicationParams>;

/** fieldId is the form's upload question id; the service checks it belongs to the application. */
export const ReviewAttachmentParams = z.strictObject({
  cycleId: Id,
  applicationId: Id,
  fieldId: z.string().min(1).max(100),
});
export type ReviewAttachmentParams = z.input<typeof ReviewAttachmentParams>;

export const ReviewMembershipParams = z.strictObject({ cycleId: Id, membershipId: Id });
export type ReviewMembershipParams = z.input<typeof ReviewMembershipParams>;

export const ReviewFlagParams = z.strictObject({ cycleId: Id, flagId: Id });
export type ReviewFlagParams = z.input<typeof ReviewFlagParams>;

export const ReviewAssignmentParams = z.strictObject({ cycleId: Id, assignmentId: Id });
export type ReviewAssignmentParams = z.input<typeof ReviewAssignmentParams>;

export const ReviewDecisionParams = z.strictObject({ cycleId: Id, decisionId: Id });
export type ReviewDecisionParams = z.input<typeof ReviewDecisionParams>;

export const ReviewReviewParams = z.strictObject({ cycleId: Id, reviewId: Id });
export type ReviewReviewParams = z.input<typeof ReviewReviewParams>;

/** Routes that take no input. */
export const ReviewEmptyBody = z.strictObject({});
export type ReviewEmptyBody = z.input<typeof ReviewEmptyBody>;

// ---------------------------------------------------------------- sign-in

/** POST /api/review/auth/dev-login (development only). */
export const ReviewDevLoginRequest = z.strictObject({ email: z.string().max(320) });
export type ReviewDevLoginRequest = z.input<typeof ReviewDevLoginRequest>;

/** GET /api/review/auth/callback: App ID's redirect; other parameters it adds are left alone. */
export const ReviewCallbackQuery = z.looseObject({ code: z.string().optional(), state: z.string().optional() });
export type ReviewCallbackQuery = z.input<typeof ReviewCallbackQuery>;

// ---------------------------------------------------------------- staff: cycles

/** POST /api/review/staff/cycles. The year's range is the service's (2023–2100). */
export const ReviewCreateCycleRequest = z.strictObject({
  year: Num.optional(),
  name: Text,
  editionIds: z.array(Id).optional(),
});
export type ReviewCreateCycleRequest = z.input<typeof ReviewCreateCycleRequest>;

/** Only the keys sent change; ranges are checked by the service. */
export const ReviewSettingsPatch = z.strictObject({
  reviewersPerApplication: Num.optional(),
  discrepancyThreshold: Num.optional(),
  selfIdDuringScoring: z.boolean().optional(),
  reviewerGroups: z.array(z.string()).optional(),
  acceptedGroups: z.array(z.string()).optional(),
  extraQuestionTiers: z.record(z.string(), z.string()).nullish(),
  retention: z
    .strictObject({
      unsuccessfulYears: Num.optional(),
      fellowshipYears: Num.optional(),
      acceptedYearsAfterFellowship: Num.optional(),
      auditYears: Num.optional(),
    })
    .nullish(),
});
export type ReviewSettingsPatch = z.input<typeof ReviewSettingsPatch>;

/**
 * The rubric as edited on the cycle page (versioned JSON). Permissive: the service validates keys,
 * scales, labels, ranges and the priority score, and keeps every other criterion field as sent.
 */
export const ReviewRubricInput = z.looseObject({
  version: z.union([z.string(), z.number()]).optional(),
  criteria: z
    .array(
      z.looseObject({
        key: z.string().optional(),
        section: z.string().optional(),
        label: z.string().optional(),
        scale: z.string().optional(),
        min: Num.optional(),
        max: Num.optional(),
      }),
    )
    .optional(),
});
export type ReviewRubricInput = z.input<typeof ReviewRubricInput>;

/** The eligibility rules (versioned JSON). Permissive: the service checks each rule type. */
export const ReviewRulesetInput = z.looseObject({
  version: z.union([z.string(), z.number()]).optional(),
  rules: z
    .array(
      z.looseObject({
        key: z.string().optional(),
        type: z.string().optional(),
        params: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .optional(),
});
export type ReviewRulesetInput = z.input<typeof ReviewRulesetInput>;

/** PATCH /api/review/staff/cycles/:cycleId */
export const ReviewUpdateCycleRequest = z.strictObject({
  name: z.string().optional(),
  editionIds: z.array(Id).optional(),
  settings: ReviewSettingsPatch.optional(),
  rubric: ReviewRubricInput.optional(),
  eligibilityRules: ReviewRulesetInput.optional(),
});
export type ReviewUpdateCycleRequest = z.input<typeof ReviewUpdateCycleRequest>;

/** POST /api/review/staff/cycles/:cycleId/transition: one step forward, or back with a reason. */
export const ReviewTransitionRequest = z.strictObject({
  to: ReviewCycleState,
  reason: Text,
  force: z.boolean().optional(),
});
export type ReviewTransitionRequest = z.input<typeof ReviewTransitionRequest>;

/** GET /api/review/staff/cycles/:cycleId/candidates?q= */
export const ReviewCandidatesQuery = z.strictObject({ q: z.string().max(200).optional() });
export type ReviewCandidatesQuery = z.input<typeof ReviewCandidatesQuery>;

/** POST /api/review/staff/cycles/:cycleId/committee: a member by userId or email, a Program Manager by adminEmail. */
export const ReviewAddMemberRequest = z.strictObject({
  seat: ReviewSeat,
  userId: Id.optional(),
  email: z.string().max(320).optional(),
  adminEmail: z.string().max(320).optional(),
  seatLabel: Text,
  otherEmails: EmailList.optional(),
});
export type ReviewAddMemberRequest = z.input<typeof ReviewAddMemberRequest>;

/** PATCH /api/review/staff/cycles/:cycleId/committee/:membershipId */
export const ReviewUpdateMemberRequest = z.strictObject({
  otherEmails: EmailList.optional(),
  seatLabel: Text,
});
export type ReviewUpdateMemberRequest = z.input<typeof ReviewUpdateMemberRequest>;

/** A reason, required by the service where the action needs one. */
export const ReviewReasonRequest = z.strictObject({ reason: Text });
export type ReviewReasonRequest = z.input<typeof ReviewReasonRequest>;

// ---------------------------------------------------------------- staff: applications

/** POST …/applications/:applicationId/eligibility; ineligible needs a note (service). */
export const ReviewResolveEligibilityRequest = z.strictObject({
  status: ReviewEligibilityResolution,
  note: Text,
});
export type ReviewResolveEligibilityRequest = z.input<typeof ReviewResolveEligibilityRequest>;

/** POST …/applications/:applicationId/choose-submission */
export const ReviewChooseSubmissionRequest = z.strictObject({
  keep: ReviewKeepSubmission,
  reason: Text,
});
export type ReviewChooseSubmissionRequest = z.input<typeof ReviewChooseSubmissionRequest>;

/** POST …/applications/:applicationId/decision (staff and chair). A conditional decision needs all three condition fields (service). */
export const ReviewDecisionRequest = z.strictObject({
  outcome: ReviewOutcome,
  rationale: Text,
  conditionText: Text,
  conditionDueDate: Text,
  conditionOwner: Text,
});
export type ReviewDecisionRequest = z.input<typeof ReviewDecisionRequest>;

/** PATCH …/decisions/:decisionId/condition (staff and chair). */
export const ReviewConditionRequest = z.strictObject({
  status: ReviewConditionStatus,
  note: Text,
});
export type ReviewConditionRequest = z.input<typeof ReviewConditionRequest>;

/** POST …/export (staff and chair); the exporter's name is set by the route. */
export const ReviewExportRequest = z.strictObject({
  reason: Text,
  includeSelfId: z.boolean().optional(),
  includeNotes: z.boolean().optional(),
});
export type ReviewExportRequest = z.input<typeof ReviewExportRequest>;

/** GET …/audit?limit=&before=; limit is capped at 1000 by the service. */
export const ReviewAuditQuery = z.strictObject({ limit: Digits.optional(), before: Digits.optional() });
export type ReviewAuditQuery = z.input<typeof ReviewAuditQuery>;

// ---------------------------------------------------------------- staff: assignments

/** POST …/assignments/approve; no ids approves every proposed assignment. */
export const ReviewApproveRequest = z.strictObject({ assignmentIds: z.array(Id).optional() });
export type ReviewApproveRequest = z.input<typeof ReviewApproveRequest>;

/** POST …/assignments */
export const ReviewAddAssignmentRequest = z.strictObject({
  applicationId: Id,
  reviewerUserId: Id,
  purpose: ReviewAssignmentPurpose.optional(),
});
export type ReviewAddAssignmentRequest = z.input<typeof ReviewAddAssignmentRequest>;

/** POST …/assignments/:assignmentId/reassign */
export const ReviewReassignRequest = z.strictObject({ reviewerUserId: Id, reason: Text });
export type ReviewReassignRequest = z.input<typeof ReviewReassignRequest>;

// ---------------------------------------------------------------- reviewer portal

export const ReviewConflictDeclaration = z.strictObject({ type: ReviewConflictType, detail: Text });
export type ReviewConflictDeclaration = z.input<typeof ReviewConflictDeclaration>;

/** POST …/assignments/:assignmentId/attest: no conflict, or one declared. */
export const ReviewAttestRequest = z.strictObject({
  noConflict: z.boolean().optional(),
  conflict: ReviewConflictDeclaration.optional(),
});
export type ReviewAttestRequest = z.input<typeof ReviewAttestRequest>;

/** One criterion's value; which part counts depends on the criterion's scale (service). */
export const ReviewScoreInput = z.strictObject({
  rating: Text,
  count: z.number().nullish(),
  score: z.union([z.number(), z.string()]).nullish(),
  text: Text,
});
export type ReviewScoreInput = z.input<typeof ReviewScoreInput>;

/** PUT …/reviews/:reviewId: the criteria that changed, by key; null clears one. */
export const ReviewSaveScoresRequest = z.strictObject({
  scores: z.record(z.string(), ReviewScoreInput.nullable()),
});
export type ReviewSaveScoresRequest = z.input<typeof ReviewSaveScoresRequest>;

/** POST …/chair/conflicts/:flagId/rule */
export const ReviewRuleConflictRequest = z.strictObject({ ruling: ReviewConflictRuling, reason: Text });
export type ReviewRuleConflictRequest = z.input<typeof ReviewRuleConflictRequest>;
